"use client"

import { Loader2 } from "lucide-react"
import { JSX, memo, useCallback } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils/ComponentProps"
import { TIcon } from "../display/TIcon"
import { TIconNameV2 } from "../display/TIconPathsV2"
import { IconSelectTrigger, Select, SelectContent, SelectGroup, SelectItem } from "../shadcn/select"
import { IDropdownProps, ISelectionProps } from "./types"

/**
 * 아이콘 셀렉트 컴포넌트
 *
 * @description
 * 아이콘만 표시하는 드롭다운 셀렉트 컴포넌트입니다.
 * 선택된 값을 텍스트로 표시하지 않고 아이콘 버튼만 보여줍니다.
 * 툴바, 필터 버튼 등 공간이 제한된 곳에 적합합니다.
 *
 * @template V - 값 타입 (string 또는 number)
 *
 * @example
 * ```tsx
 * // 기본 사용 - 필터 버튼
 * <TIconSelect
 *   name="filter"
 *   items={[
 *     { value: "all", label: "전체" },
 *     { value: "active", label: "활성" },
 *     { value: "inactive", label: "비활성" }
 *   ]}
 *   value={filter}
 *   onChange={setFilter}
 * />
 *
 * // 툴바 버튼
 * <TIconSelect
 *   name="moreVertical"
 *   size="small"
 *   color="ghost"
 *   items={[
 *     { value: "edit", label: "수정" },
 *     { value: "delete", label: "삭제" }
 *   ]}
 *   value={action}
 *   onChange={setAction}
 * />
 *
 * // 정렬 버튼
 * <TIconSelect
 *   name="arrowUpDown"
 *   items={[
 *     { value: "asc", label: "오름차순" },
 *     { value: "desc", label: "내림차순" }
 *   ]}
 *   value={sortOrder}
 *   onChange={setSortOrder}
 * />
 * ```
 */
interface TIconSelectProps<V extends string | number> extends ISelectionProps<V>, IDropdownProps, BaseInteractiveProps {
	/** 표시할 아이콘 이름 */
	name?: TIconNameV2
	/** 아이콘 버튼 크기 */
	size?: "small" | "medium"
	/** 색상 variant */
	color?: "default" | "ghost"
}

function TIconSelectInner<V extends string | number>(props: TIconSelectProps<V>): JSX.Element {
	const {
		// 기본 속성
		id,
		className,

		// 데이터 관련
		items,
		value,

		// 상태 관련
		disabled = false,
		loading = false,

		// UI 관련
		name = "filter",
		size = "small",
		color = "default",

		// 드롭다운 설정
		open,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,

		// 이벤트 핸들러
		onChange,
		onOpenChange,

		// 기타
		dataAttributes,
		...restProps
	} = props

	const dataProps = useDataAttributes(dataAttributes)

	const handleSelect = useCallback(
		(stringValue: string) => {
			const item = items.find((item) => String(item.value) === stringValue)
			if (item === undefined) return

			onChange(item.value)
		},
		[items, onChange]
	)

	const selectedValue = value !== undefined ? String(value) : undefined
	const hasItems = items.length > 0
	const isDisabled = loading || disabled || !hasItems

	// 개발 환경에서 빈 items와 value prop 불일치 경고
	if (process.env.NODE_ENV === "development" && !hasItems && value !== undefined) {
		console.warn(`TIconSelect: 'value' prop is provided (${value}) but 'items' is empty. The value will be ignored until items are provided.`)
	}

	return (
		<div id={id} className={className} {...restProps}>
			<Select value={selectedValue} onValueChange={handleSelect} disabled={isDisabled} open={open} onOpenChange={onOpenChange} {...dataProps}>
				<IconSelectTrigger disabled={isDisabled} data-size={size} color={color}>
					{loading ? <Loader2 aria-hidden="true" className="loader" /> : <TIcon className="icon" name={name} size={20} />}
				</IconSelectTrigger>
				<SelectContent align={align} alignOffset={alignOffset} side={side} sideOffset={sideOffset} position="popper">
					<SelectGroup>
						{items.map((item) => (
							<SelectItem key={String(item.value)} value={String(item.value)} disabled={item.disabled}>
								{item.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	)
}

const TIconSelectMemo = memo(TIconSelectInner)
TIconSelectMemo.displayName = "TIconSelect"

export const TIconSelect = TIconSelectMemo as typeof TIconSelectInner
